import { useEffect, useState } from "react";

export type Theme = "light" | "dark";

const STORAGE_KEY = "mp-theme";

/**
 * Light/dark theme controller. Default is "light". The chosen theme is
 * persisted in localStorage and applied as a `.dark` class on <html>, which
 * drives every semantic design token in src/styles.css. An inline script in
 * the document head (see __root.tsx) applies the saved theme before first
 * paint to avoid a flash.
 *
 * The first render is intentionally fixed to "light" so the client hydration
 * markup matches the server output (which has no access to localStorage). The
 * real theme is read on mount; this avoids a hydration mismatch that would
 * otherwise force React to regenerate the whole tree (causing layout reflows).
 */
export function useTheme() {
  const [theme, setThemeState] = useState<Theme>("light");
  const [hydrated, setHydrated] = useState(false);

  // On mount, adopt the theme the inline pre-paint script already applied.
  useEffect(() => {
    let stored: string | null = null;
    try {
      stored = localStorage.getItem(STORAGE_KEY);
    } catch {
      /* localStorage unavailable (private mode) — ignore */
    }
    const initial: Theme =
      stored === "dark" ||
      (stored == null && document.documentElement.classList.contains("dark"))
        ? "dark"
        : "light";
    setThemeState(initial);
    setHydrated(true);
  }, []);

  // Apply theme to the DOM + persist, but only after hydration so we don't
  // fight the inline pre-paint script on the very first render.
  useEffect(() => {
    if (!hydrated) return;
    const root = document.documentElement;
    if (theme === "dark") root.classList.add("dark");
    else root.classList.remove("dark");
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      /* localStorage unavailable (private mode) — ignore */
    }
  }, [theme, hydrated]);

  const setTheme = (t: Theme) => setThemeState(t);
  const toggleTheme = () => setThemeState((t) => (t === "dark" ? "light" : "dark"));

  return { theme, isDark: theme === "dark", setTheme, toggleTheme };
}
